import { attr, repeated, ProtoBaseModel } from '@square/dashboard-data/utils/ember-model-stubs';
import DeviceProfileSettingsV2 from './settings/device-profile-settings-v2';
import DeviceProfileLink from '@square/dashboard-data/models/device-profiles-v2/device-profile-link';
import { DeviceProfileType } from '@square/dashboard-protos/models/squareup/devicesettings/profiles/v2/model/shared/device_profile_type';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { A } from '@ember/array';
import { isPresent } from '@ember/utils';
import Enum from '@square/dashboard-utilities/utils/enum';
import { camelize, underscore } from '@square/dashboard-utilities/utils/keys';
import { DeviceProfile as DeviceProfileProto } from '@square/dashboard-protos/models/squareup/devicesettings/profiles/v2/model/shared/device_profile';

// DPV2 INTEGRATION POINT: add new verticals here for serialization & deserialization
const PROFILE_TYPE_TO_SETTINGS_PROTO_KEY = {
  [DeviceProfileType.SPOS]: 'spos_settings',
  [DeviceProfileType.RESTAURANT]: 'rst_settings',
  [DeviceProfileType.RESTAURANT_MOBILE]: 'rst_mobile_settings',
  [DeviceProfileType.RETAIL]: 'rtl_settings',
  [DeviceProfileType.APPOINTMENTS]: 'apos_settings',
  [DeviceProfileType.KDS_EXPO]: 'kds_expo_settings',
  [DeviceProfileType.KDS]: 'kds_settings',
  [DeviceProfileType.SQUARE_HARDWARE]: 'square_hardware_settings',
  [DeviceProfileType.TERMINAL_API]: 'terminal_api_settings',
  [DeviceProfileType.RELEASE_MANAGER]: 'release_manager_settings',
  [DeviceProfileType.FNBKIOSK]: 'fnb_kiosk_settings',
  [DeviceProfileType.INVOICES]: 'ipos_settings'
};
export const DeviceProfileTypeKeys = new Enum(Object.keys(DeviceProfileType));
export default ProtoBaseModel.extend({
  id: attr(String),
  shared: attr(Boolean, {
    defaultValue: true
  }),
  version: attr(Number),
  name: attr(String),
  profileSettings: attr(DeviceProfileSettingsV2, {
    defaultValue() {
      return DeviceProfileSettingsV2.create();
    }
  }),
  type: attr(DeviceProfileType),
  deviceLinks: repeated(DeviceProfileLink, {
    defaultValue: A([])
  }),
  linkedLocations: computed('deviceLinks', 'deviceLinks.@each.device', function () {
    return A([...new Set(this.deviceLinks.map(link => link.device.locationId))]);
  }),
  deviceLinkCount: alias('deviceLinks.length'),
  isCopy: attr(Boolean, {
    defaultValue: false
  }),
  convertToJson() {
    // Remove unwanted properties before we send the request to the server.
    const data = this.cleanupRequest(this.toJSON());
    const protoKey = PROFILE_TYPE_TO_SETTINGS_PROTO_KEY[DeviceProfileType[data.type]];
    if (!isPresent(protoKey)) {
      throw new Error(`Unsupported vertical type ${data.type} for serialization`);
    }
    data[protoKey] = this.profileSettings.convertToJson();
    return data;
  },
  cleanupRequest({
    id,
    shared,
    version,
    name,
    type
  }) {
    // Properties to keep
    return {
      id,
      shared,
      version,
      name,
      type
    };
  }
}).reopenClass({
  convertToModel(profile) {
    const data = this.deserialize(profile);
    const protoKey = PROFILE_TYPE_TO_SETTINGS_PROTO_KEY[DeviceProfileType[data.type]];
    if (!isPresent(protoKey)) {
      throw new Error(`Unsupported vertical type ${data.type} for de-serialization`);
    }
    data.set('profileSettings', DeviceProfileSettingsV2.toDeviceProfileSettingsV2(profile[protoKey]));
    return data;
  },
  createACopy(profile, newName) {
    const copyProfile = this.convertToModel({
      ...profile.convertToJson(),
      name: newName
    });
    copyProfile.set('isCopy', true);
    return copyProfile;
  },
  fromProto(proto) {
    if (!proto) {
      return null;
    }
    return this.convertToModel(underscore(proto.toJSON()));
  },
  toProto(obj) {
    return DeviceProfileProto.fromObject(camelize(obj.convertToJson()));
  }
});